import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart } from 'lucide-react';
import NewsletterSignup from './NewsletterSignup';
import AppInstallCard from './AppInstallCard';
import { CUSTOMERS_LABEL, STATIONS_LABEL, HOTELS_SHORT, BENEFITS_TOTAL, BENEFITS_INCLUDED } from '../config/productFacts';

const FOOTER_COLS = [
  {
    title: 'Privat',
    links: [
      { label: 'Silver Card', to: '/silver-card' },
      { label: 'Vorteile', to: '/vorteile' },
      { label: 'Preise', to: '/preise' },
      { label: 'Silver vs. Gold', to: '/vergleich' },
      { label: 'Vorteilsrechner', to: '/sparen' },
      { label: 'Kilometer-Rechner', to: '/kilometer-rechner' },
      { label: 'So funktioniert\'s', to: '/so-funktionierts' },
    ],
  },
  {
    title: 'Business',
    links: [
      { label: 'Gold Card', to: '/gold-card' },
      { label: 'Fuhrpark-Lösung', to: '/business/fuhrpark' },
      { label: 'Flottenvorteile', to: '/business/flottenvorteile' },
      { label: 'Business-Rechner', to: '/business/rechner' },
      { label: 'Mitarbeiter-Benefit', to: '/business/mitarbeiter-benefit' },
      { label: 'Geschäftsreisen', to: '/business/geschaeftsreisen' },
      { label: 'Flottenversicherung', to: '/flottenversicherung' },
    ],
  },
  {
    title: 'Reisen',
    links: [
      { label: 'Hotel buchen', to: '/hotel-suche' },
      { label: 'FuelX Hotels', to: '/fuelx-hotels' },
      { label: 'Luxushotels', to: '/luxushotels' },
      { label: 'Geschäftsreisen', to: '/geschaeftsreisen' },
    ],
  },
  {
    title: 'Auto-Services',
    links: [
      { label: 'KFZ-Versicherung', to: '/versicherung' },
      { label: 'Autokredit', to: '/autokredit' },
      { label: 'Auto verkaufen', to: '/auto-verkaufen' },
      { label: 'TÜV-Service', to: '/tuev-service' },
      { label: 'KFZ-Steuer zurück', to: '/kfz-steuer' },
      { label: 'Öl-Service', to: '/oel-service' },
      { label: 'Reifen-Wechsel', to: '/reifen-wechsel' },
      { label: 'Pannenhilfe', to: '/pannenhilfe' },
      { label: 'Werkstatt & Reparatur', to: '/werkstatt' },
      { label: 'Werkstatt in der Nähe', to: '/werkstatt-in-der-naehe' },
      { label: 'Tankstellen-Preise', to: '/tankstellen' },
      { label: 'Tankstellen in der Nähe', to: '/tankstellen-in-meiner-naehe' },
      { label: 'Spritpreise aktuell', to: '/spritpreise' },
      { label: 'Günstig tanken', to: '/guenstig-tanken' },
      { label: 'Spritkostenrechner', to: '/spritkostenrechner' },
      { label: 'Autokosten-Rechner', to: '/autokosten' },
      { label: 'Pendlerkosten-Rechner', to: '/pendlerkosten-rechner' },
      { label: 'Tankkarte Vergleich', to: '/tankkarte' },
    ],
  },
  {
    title: 'Über FuelX',
    links: [
      { label: 'Über uns', to: '/ueber-uns' },
      { label: 'Karriere', to: '/karriere' },
      { label: 'Kontakt', to: '/kontakt' },
      { label: 'Presse', to: '/presse' },
      { label: 'Tankstellen-Netzwerk', to: '/tankstellen-netzwerk' },
      { label: 'FAQ', to: '/faq' },
      { label: 'FuelX Erfahrungen', to: '/bewerten' },
      { label: 'FuelX Ratgeber', to: '/ratgeber' },
      { label: 'Ratgeber: Wann tanken?', to: '/ratgeber/wann-tanken-am-guenstigsten' },
      { label: 'Ratgeber: Autokosten senken', to: '/ratgeber/autokosten-senken' },
      { label: 'Ratgeber: Werkstattkosten prüfen', to: '/ratgeber/werkstattkosten-pruefen' },
    ],
  },
  {
    title: 'Rechtliches',
    links: [
      { label: 'Datenschutz', to: '/datenschutz' },
      { label: 'AGB', to: '/agb' },
      { label: 'Impressum', to: '/impressum' },
      { label: 'Widerrufsrecht', to: '/widerruf' },
      { label: 'Vertrag widerrufen', to: '/vertrag-widerrufen' },
      { label: 'Zahlung & Lieferung', to: '/zahlung-und-lieferung' },
      { label: 'Barrierefreiheit', to: '/barrierefreiheit' },
      { label: 'Cookie-Richtlinie', to: '/cookies' },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="bg-white border-t border-stone-100" data-testid="global-footer">
      {/* Newsletter — schön gestalteter Footer-Bereich */}
      <div className="border-b border-stone-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 sm:py-20">
          <div className="rounded-[2rem] bg-stone-950 relative overflow-hidden p-10 sm:p-14 lg:p-16 border border-stone-800">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(62,255,126,0.18),_transparent_55%)]" aria-hidden="true" />
            <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full opacity-30 blur-3xl pointer-events-none"
              style={{ background: 'radial-gradient(circle, rgba(62,255,126,0.4) 0%, transparent 70%)' }}
              aria-hidden="true"
            />
            <div className="relative grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-400/15 border border-emerald-400/40 px-3.5 py-1.5 mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(62,255,126,0.9)] animate-pulse" />
                  <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-300">FuelX Newsletter</p>
                </div>
                <h3 className="font-display text-white text-3xl sm:text-4xl lg:text-5xl tracking-[-0.025em] leading-[1.0]">
                  Bleib auf dem Laufenden.
                  <span className="block text-emerald-400 mt-2">Kilometer, Hotels, Auto-News.</span>
                </h3>
                <p className="text-sm sm:text-base text-stone-500 leading-relaxed mt-5 max-w-md">
                  Neue Card-Vorteile, Spritpreis-Trends und Hotel-Deals — kompakt per E-Mail. Kein Spam, jederzeit abbestellbar.
                </p>
              </div>
              <div className="lg:pl-6">
                <NewsletterSignup variant="footer-hero" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust/Stats row */}
      <div className="border-b border-stone-100 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            [CUSTOMERS_LABEL, 'Nutzer europaweit'],
            [STATIONS_LABEL, 'Tankstellen'],
            [HOTELS_SHORT, 'Hotels weltweit'],
            [String(BENEFITS_TOTAL), `Leistungen · ${BENEFITS_INCLUDED} ab Tag 1`],
          ].map(([v, l], i) => (
            <div key={i}>
              <p className="text-2xl font-medium tracking-tight text-stone-900 tabular-nums">{v}</p>
              <p className="text-xs text-stone-500 mt-1">{l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* App-Install — prominenter Download-Bereich */}
      <div className="border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 sm:py-14">
          <AppInstallCard testId="footer-app-section" />
        </div>
      </div>

      {/* Links Grid */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-10">
          {FOOTER_COLS.map((col) => (
            <div key={col.title}>
              <p className="text-xs font-medium uppercase tracking-[0.15em] text-stone-900 mb-6">{col.title}</p>
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link to={link.to} className="text-sm text-stone-500 hover:text-stone-900 transition-colors">
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-100 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="footer-bottom-bar">
          <div className="flex items-center gap-6">
            <img loading="lazy" decoding="async" src="/fuelx-logo-final-cropped-96.webp" alt="FuelX – Autoplattform und FuelX Card" width="891" height="216" className="h-5 w-auto" />
            <span className="text-xs text-stone-500" data-testid="footer-copyright">&copy; 2026 FuelX<sup>®</sup> · fuelxcard.com · Eingetragene Marke · <span className="italic text-stone-500">You fuel good.</span></span>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs text-stone-500" data-testid="footer-made-with-love">
            Made with <Heart className="w-3.5 h-3.5 text-rose-500 fill-rose-500" strokeWidth={0} /> in Stuttgart
          </span>
          <div className="flex items-center gap-4">
            {['Visa', 'Mastercard', 'SEPA', 'Stripe', 'SSL'].map(n => (
              <span key={n} className="text-[10px] font-medium uppercase tracking-wider text-stone-500">{n}</span>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pb-8 -mt-2" />
      </div>
    </footer>
  );
}
